/**
 * API Configuration Constants
 *
 * Centralized configuration for API clients.
 */

/**
 * Retry configuration for failed API requests. Only reads (GET/HEAD) are
 * retried, and quickly: a failure surfaces in about 2 s rather than stacking
 * retries for a quarter of a minute. React Query does not retry on top.
 */
export const API_RETRY_CONFIG = {
  /** Maximum number of retry attempts */
  maxRetries: 2,
  /** Base delay in milliseconds before retrying (~0.5 s, then ~1 s) */
  baseDelayMs: 500,
  /** Maximum delay in milliseconds between retries */
  maxDelayMs: 2000,
  /** A 429 asking the client to wait longer than this is not retried: it
   * surfaces straight away and the rate-limit event tells the user. */
  maxRetryAfterSeconds: 2,
  /** Jitter range (±percentage) to add randomness to retry delays */
  jitterRange: 0.25,
} as const;

/** HTTP status codes */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  BAD_GATEWAY: 502,
  SERVICE_UNAVAILABLE: 503,
  GATEWAY_TIMEOUT: 504,
} as const;

/** Retryable HTTP status codes */
export const RETRYABLE_STATUS_CODES = [
  HTTP_STATUS.TOO_MANY_REQUESTS,
  HTTP_STATUS.INTERNAL_SERVER_ERROR,
  HTTP_STATUS.BAD_GATEWAY,
  HTTP_STATUS.SERVICE_UNAVAILABLE,
  HTTP_STATUS.GATEWAY_TIMEOUT,
] as const;

/** API timeout configuration */
export const API_TIMEOUT_CONFIG = {
  /** Default request timeout in milliseconds (writes) */
  defaultMs: 30000,
  /** Timeout for reads (GET/HEAD) in milliseconds — a hung server turns
   * into an error on screen after this long */
  readMs: 15000,
  /** Long-running request timeout in milliseconds */
  longRunningMs: 60000,
  /** File upload timeout in milliseconds */
  uploadMs: 120000,
} as const;

/**
 * Calculate exponential backoff delay with jitter
 */
export function calculateRetryDelay(attempt: number, retryAfterSeconds?: number): number {
  if (retryAfterSeconds) {
    return retryAfterSeconds * 1000;
  }

  const { baseDelayMs, maxDelayMs, jitterRange } = API_RETRY_CONFIG;
  const exponentialDelay = Math.min(baseDelayMs * (2 ** attempt), maxDelayMs);

  // Add jitter (±jitterRange)
  const jitterMultiplier = 1 - jitterRange + Math.random() * (jitterRange * 2);
  return exponentialDelay * jitterMultiplier;
}

/**
 * Check if an HTTP status code is retryable
 */
export function isRetryableStatusCode(statusCode: number): boolean {
  return (RETRYABLE_STATUS_CODES as readonly number[]).includes(statusCode);
}
